import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { motion } from 'framer-motion';
import { APP_VERSION } from '@/version';
import {
  Eye, EyeOff, Loader2, Globe, Users, Clock, CalendarCheck,
  ShieldCheck, ArrowRight, ArrowLeft
} from 'lucide-react';

const LOGO_URL = '/branding/logo.png';
const PATTERN_URL = 'https://lucent.com.sa/wp-content/uploads/2022/08/Pattern.svg';

/* -------------------------------------------------------------------------- */
/*  Lucent HR — Single-Portal Login                                            */
/*  Recruitment & Government Relations are no longer entry-portals; they      */
/*  live as cards inside the post-login admin dashboard.                       */
/* -------------------------------------------------------------------------- */

const ACCENT = '#655dc6';
const ACCENT_DARK = '#4A40A5';

/* Highlight bullets shown on the brand panel — emphasise the HR product
   so the login surface reads like the "front door" of an HR suite, not a
   generic auth screen. */
const HIGHLIGHTS = [
  {
    icon: Users,
    title_ar: 'إدارة الموظفين',
    title_en: 'Employee Management',
    desc_ar: 'ملفات شخصية · هياكل تنظيمية · صلاحيات دقيقة',
    desc_en: 'Profiles · Org charts · Granular permissions',
  },
  {
    icon: Clock,
    title_ar: 'الحضور والورديات',
    title_en: 'Attendance & Shifts',
    desc_ar: 'بصمة جغرافية ذكية · ورديات متعددة · تنبيهات أمان',
    desc_en: 'Smart geofence · Multi-shifts · Security alerts',
  },
  {
    icon: CalendarCheck,
    title_ar: 'الإجازات والمخالصات',
    title_en: 'Leaves & Clearances',
    desc_ar: 'سير اعتماد متعددة · أرصدة فورية · إشعارات ثنائية اللغة',
    desc_en: 'Multi-stage approvals · Live balances · Bilingual alerts',
  },
  {
    icon: ShieldCheck,
    title_ar: 'حوكمة وامتثال',
    title_en: 'Governance & Compliance',
    desc_ar: 'متوافق مع نظام العمل · سجلات تدقيق · تشفير كامل',
    desc_en: 'Saudi-law aligned · Audit trails · End-to-end encrypted',
  },
];

export default function LandingPage() {
  const { language, toggleLanguage } = useLanguage();
  const { login, verifyOtp, resendOtp } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAr = language === 'ar';

  // Deep-link target preserved by ProtectedRoute when an unauthenticated
  // user hits a protected page (e.g. the "افتح الاستبيان" button in an
  // email). Only accept relative in-app paths to avoid open-redirect
  // abuse.
  const resolveNext = () => {
    const raw = searchParams.get('next') || '';
    if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
    return '/home';
  };

  const badgeStyle = isDark
    ? { background: '#2c2650', color: '#b4abf2' }
    : { background: '#E8E6F5', color: ACCENT_DARK };

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Admin 2FA (OTP) state
  const [otpStep, setOtpStep] = useState(false);
  const [challengeId, setChallengeId] = useState('');
  const [emailMasked, setEmailMasked] = useState('');
  const [otp, setOtp] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.otp_required) {
        // Second-factor needed — switch to OTP screen.
        setChallengeId(data.challenge_id);
        setEmailMasked(data.email_masked || '');
        setOtp('');
        setOtpStep(true);
      } else {
        navigate(resolveNext());
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 400) {
        setError(isAr ? 'البريد الإلكتروني أو كلمة المرور غير صحيحة' : 'Invalid email or password');
      } else {
        setError(err.response?.data?.detail || (isAr ? 'تعذّر الاتصال بالخادم' : 'Could not reach the server'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyOtp(challengeId, otp, rememberDevice);
      navigate(resolveNext());
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (isAr ? 'فشل التحقق من الرمز' : 'Verification failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendMsg('');
    setError('');
    setResending(true);
    try {
      const data = await resendOtp(challengeId);
      setChallengeId(data.challenge_id);
      setEmailMasked(data.email_masked || emailMasked);
      setOtp('');
      setResendMsg(isAr ? 'تم إرسال رمز جديد إلى بريدك.' : 'A fresh code was sent to your email.');
    } catch (err) {
      setError(err.response?.data?.detail || (isAr ? 'فشل إرسال الرمز' : 'Failed to resend code'));
    } finally {
      setResending(false);
    }
  };

  const handleBackToPassword = () => {
    setOtpStep(false);
    setChallengeId('');
    setEmailMasked('');
    setOtp('');
    setError('');
    setResendMsg('');
  };

  return (
    <div
      className="min-h-screen w-full flex items-stretch relative overflow-hidden"
      style={{
        background: isDark
          ? 'linear-gradient(135deg, #14121f 0%, #17142a 50%, #1a1630 100%)'
          : 'linear-gradient(135deg, #F5F3FA 0%, #ECE9F7 50%, #E5E1F4 100%)',
      }}
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="landing-page"
    >
      {/* Decorative pattern + brand halos */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${PATTERN_URL})`,
          backgroundRepeat: 'repeat',
          backgroundSize: '300px',
          opacity: 0.045,
        }}
      />
      <div
        aria-hidden
        className="absolute top-[-15%] start-[-10%] w-[520px] h-[520px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(101, 93, 198, 0.20)' }}
      />
      <div
        aria-hidden
        className="absolute bottom-[-15%] end-[-10%] w-[520px] h-[520px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(212, 168, 67, 0.12)' }}
      />

      {/* Language toggle — placed on the OPPOSITE side of the reading flow
          (top-end → top-left in RTL, top-right in LTR) so it stays out of the
          way of the primary brand mark. */}
      <button
        onClick={toggleLanguage}
        className="absolute top-6 end-6 z-30 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-white/80 bg-white/70 backdrop-blur-md text-[#655dc6] hover:bg-white hover:text-[#544cb0] text-xs font-semibold tracking-wide shadow-sm transition-all"
        data-testid="language-toggle"
      >
        <Globe size={14} />
        {isAr ? 'EN' : 'عربي'}
      </button>

      {/* Desktop footer — centered along the bottom edge. Mobile footer is
          inline inside the login card. */}
      <p
        className="hidden lg:block absolute bottom-6 inset-x-0 z-20 text-center text-xs text-[#9CA3AF] font-medium tracking-wide"
        data-testid="page-footer"
      >
        © {new Date().getFullYear()} Lucent Dental Center · {isAr ? 'جميع الحقوق محفوظة' : 'All rights reserved'}
        <span className="mx-2 text-[#C4BFF0]">·</span>
        <span data-testid="app-version">v{APP_VERSION}</span>
      </p>

      {/* Layout: 2-column on lg+, stacked on mobile */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] w-full min-h-screen">
        {/* ── Brand / Feature panel ─────────────────────────────────────── */}
        <motion.aside
          initial={{ opacity: 0, x: isAr ? 30 : -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:flex flex-col justify-between px-12 xl:px-20 py-14"
        >
          {/* Top: brand — logo + divider + subtitle only. The "Lucent" word
              is intentionally omitted since the wordmark is already inside
              the logo image; repeating it added visual noise. */}
          <div className="flex items-center gap-4">
            <img
              src={LOGO_URL}
              alt="Lucent"
              className="h-16 w-auto object-contain"
              style={{ imageRendering: 'crisp-edges' }}
              data-testid="brand-logo"
            />
            <div className="h-10 w-px bg-[#655dc6]/20" />
            <span className="text-[13px] text-[#4B5563] tracking-wide font-medium">
              {isAr ? 'نظام شؤون الموظفين' : 'HR Management System'}
            </span>
          </div>

          {/* Middle: headline + highlights */}
          <div className="my-auto py-14 max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <span
                className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2.5 py-1 rounded-full mb-5"
                style={badgeStyle}
              >
                {isAr ? 'بوابة شؤون الموظفين' : 'Employee Affairs Portal'}
              </span>
              <h1
                className="text-2xl xl:text-3xl font-bold text-[#1F2937] leading-[1.25] tracking-tight mb-5 whitespace-nowrap"
              >
                {isAr
                  ? 'مرحلة جديدة من إدارة المنشأة'
                  : 'A modern HR control room'}
              </h1>
              <p className="text-base text-[#4B5563] leading-relaxed max-w-lg">
                {isAr
                  ? 'منصة لوسنت تجمع شؤون الموظفين والحضور والإجازات والرواتب والمخالصات في تجربة واحدة سلسة — مع امتثال كامل لنظام العمل السعودي.'
                  : 'Lucent unifies people, attendance, leaves, payroll and clearances in one seamless experience — fully aligned with Saudi labor law.'}
              </p>
            </motion.div>

            <div className="mt-10 grid grid-cols-2 gap-4">
              {HIGHLIGHTS.map((h, i) => {
                const Icon = h.icon;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 + i * 0.08, duration: 0.5 }}
                    className="flex gap-3 items-start"
                    data-testid={`highlight-${i}`}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md"
                      style={{ background: ACCENT }}
                    >
                      <Icon size={18} className="text-white" strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-bold text-[#1F2937] leading-tight">
                        {isAr ? h.title_ar : h.title_en}
                      </p>
                      <p className="text-[11px] text-[#6B7280] mt-1 leading-snug">
                        {isAr ? h.desc_ar : h.desc_en}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Bottom slot intentionally empty — footer is rendered once at
              the page level (bottom-end) so it sits on the opposite side of
              the brand mark, regardless of language direction. */}
          <div aria-hidden />
        </motion.aside>

        {/* ── Login form panel ──────────────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, x: isAr ? -30 : 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center justify-center px-5 sm:px-10 lg:px-14 py-12 lg:py-0"
        >
          {/* Mobile brand strip (only visible <lg) */}
          <div className="lg:hidden flex flex-col items-center gap-3 mb-8">
            <img
              src={LOGO_URL}
              alt="Lucent"
              className="h-14 w-auto object-contain"
              style={{ imageRendering: 'crisp-edges' }}
              data-testid="brand-logo-mobile"
            />
            <span
              className="text-[10px] font-bold tracking-[0.18em] uppercase px-2.5 py-1 rounded-full"
              style={badgeStyle}
            >
              {isAr ? 'بوابة شؤون الموظفين' : 'Employee Affairs Portal'}
            </span>
          </div>

          {/* Card */}
          <div className="w-full max-w-md">
            <div className="relative rounded-2xl border border-white dark:border-[#322b58] bg-white/95 backdrop-blur-2xl shadow-[0_24px_60px_-15px_rgba(101,93,198,0.25)] p-8 md:p-10 overflow-hidden">
              {/* Soft accent glow */}
              <div
                className="absolute -top-24 -end-24 w-56 h-56 rounded-full blur-3xl pointer-events-none"
                style={{ background: 'rgba(101, 93, 198, 0.18)' }}
              />
              {/* Top accent strip */}
              <div
                className="absolute inset-x-0 top-0 h-1"
                style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)` }}
              />

              <div className="relative">
                <h2 className="text-2xl md:text-[28px] font-bold text-[#1F2937] leading-tight tracking-tight">
                  {otpStep
                    ? (isAr ? 'التحقق بخطوتين' : 'Two-step verification')
                    : (isAr ? 'تسجيل الدخول' : 'Sign in')}
                </h2>
                <p className="text-xs text-[#6B7280] mt-1.5">
                  {otpStep
                    ? (isAr
                        ? `أرسلنا رمزاً مكوناً من 6 أرقام إلى ${emailMasked || 'بريدك المسجّل'}. أدخله هنا لإكمال الدخول.`
                        : `A 6-digit code was sent to ${emailMasked || 'your registered email'}. Enter it below.`)
                    : (isAr
                        ? 'استخدم بريدك الإلكتروني للوصول إلى لوحتك.'
                        : 'Use your email to access your dashboard.')}
                </p>

                {error && (
                  <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-red-700 text-sm" data-testid="login-error">
                    {error}
                  </div>
                )}
                {otpStep && resendMsg && !error && (
                  <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-emerald-700 text-sm" data-testid="login-resend-ok">
                    {resendMsg}
                  </div>
                )}

                {otpStep ? (
                  <form onSubmit={handleVerifyOtp} className="mt-6 space-y-5" data-testid="otp-form">
                    <div>
                      <label className="block text-xs font-semibold tracking-wide text-[#4B5563] mb-2 uppercase">
                        {isAr ? 'رمز التحقق (6 أرقام)' : 'Verification code (6 digits)'}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d{6}"
                        maxLength={6}
                        autoFocus
                        required
                        autoComplete="one-time-code"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        dir="ltr"
                        className="w-full h-14 px-4 text-2xl font-mono tracking-[12px] rounded-lg bg-[#FAFAFB] border border-[#E5E7EB] text-[#1F2937] placeholder-[#C4BFF0] focus:outline-none focus:border-transparent focus:ring-2 focus:bg-white transition-all"
                        style={{ '--tw-ring-color': ACCENT, textAlign: 'center', textIndent: '12px' }}
                        data-testid="otp-input"
                      />
                    </div>

                    <label className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer select-none" data-testid="otp-remember-label">
                      <input
                        type="checkbox"
                        checked={rememberDevice}
                        onChange={(e) => setRememberDevice(e.target.checked)}
                        className="w-4 h-4 accent-[#655dc6]"
                        data-testid="otp-remember-checkbox"
                      />
                      <span>{isAr ? 'تذكر هذا الجهاز لمدة 30 يوماً' : 'Remember this device for 30 days'}</span>
                    </label>

                    <button
                      type="submit"
                      disabled={loading || otp.length !== 6}
                      className="w-full h-12 rounded-lg font-semibold text-white transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
                      style={{
                        background: ACCENT,
                        boxShadow: '0 10px 28px rgba(101, 93, 198, 0.30)',
                      }}
                      onMouseEnter={(e) => { if (!loading && otp.length === 6) e.currentTarget.style.background = ACCENT_DARK; }}
                      onMouseLeave={(e) => { if (!loading && otp.length === 6) e.currentTarget.style.background = ACCENT; }}
                      data-testid="otp-submit-button"
                    >
                      {loading
                        ? <Loader2 size={20} className="animate-spin" />
                        : <span>{isAr ? 'تأكيد الرمز ودخول' : 'Verify & Sign in'}</span>}
                    </button>

                    <div className="flex items-center justify-between text-xs">
                      <button
                        type="button"
                        onClick={handleBackToPassword}
                        className="text-[#655dc6] hover:text-[#4b44a8] font-medium"
                        data-testid="otp-back-btn"
                      >
                        {isAr ? '← تعديل البريد / كلمة المرور' : '← Change email / password'}
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={resending}
                        className="text-[#655dc6] hover:text-[#4b44a8] font-medium disabled:opacity-60"
                        data-testid="otp-resend-btn"
                      >
                        {resending
                          ? (isAr ? 'جارٍ الإرسال...' : 'Sending...')
                          : (isAr ? 'إرسال رمز جديد' : 'Resend code')}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                  {/* Email */}
                  <div>
                    <label className="block text-xs font-semibold tracking-wide text-[#4B5563] mb-2 uppercase">
                      {isAr ? 'البريد الإلكتروني' : 'Email'}
                    </label>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@lucent.com.sa"
                      dir="ltr"
                      className="w-full h-12 px-4 rounded-lg bg-[#FAFAFB] border border-[#E5E7EB] text-[#1F2937] placeholder-[#9CA3AF] focus:outline-none focus:border-transparent focus:ring-2 focus:bg-white transition-all"
                      style={{ '--tw-ring-color': ACCENT }}
                      data-testid="login-email-input"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-xs font-semibold tracking-wide text-[#4B5563] mb-2 uppercase">
                      {isAr ? 'كلمة المرور' : 'Password'}
                    </label>
                    <div className="relative">
                      <input
                        type={showPw ? 'text' : 'password'}
                        required
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        dir="ltr"
                        className="w-full h-12 px-4 pe-12 rounded-lg bg-[#FAFAFB] border border-[#E5E7EB] text-[#1F2937] placeholder-[#9CA3AF] focus:outline-none focus:border-transparent focus:ring-2 focus:bg-white transition-all"
                        style={{ '--tw-ring-color': ACCENT }}
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw(!showPw)}
                        className={`absolute ${isAr ? 'start-3' : 'end-3'} top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563] transition-colors`}
                        data-testid="toggle-password-visibility"
                        aria-label={isAr ? (showPw ? 'إخفاء كلمة المرور' : 'عرض كلمة المرور') : (showPw ? 'Hide password' : 'Show password')}
                      >
                        {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-lg font-semibold text-white transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
                    style={{
                      background: ACCENT,
                      boxShadow: '0 10px 28px rgba(101, 93, 198, 0.30)',
                    }}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = ACCENT_DARK; }}
                    onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = ACCENT; }}
                    data-testid="login-submit-button"
                  >
                    {loading
                      ? <Loader2 size={20} className="animate-spin" />
                      : (
                        <>
                          <span>{isAr ? 'تسجيل الدخول' : 'Sign in'}</span>
                          {isAr ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                        </>
                      )
                    }
                  </button>
                  </form>
                )}

                <p className="text-[11px] text-[#9CA3AF] text-center mt-6 leading-relaxed">
                  {isAr
                    ? 'تواجه مشكلة في الدخول؟ تواصل مع مسؤول الموارد البشرية.'
                    : 'Trouble signing in? Contact your HR administrator.'}
                </p>
              </div>
            </div>

            {/* Footnote (mobile only — desktop has its own in the aside) */}
            <p className="lg:hidden text-center text-[11px] text-[#9CA3AF] mt-6 font-medium">
              © {new Date().getFullYear()} Lucent Dental Center · {isAr ? 'جميع الحقوق محفوظة' : 'All rights reserved'}
              <span className="mx-2 text-[#C4BFF0]">·</span>
              <span data-testid="app-version-mobile">v{APP_VERSION}</span>
            </p>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
